import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SiteCard from '@/components/base/SiteCard';
import { categories, categoryColorMap } from '@/mocks/categories';
import { sites } from '@/mocks/sites';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'yule');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab),
    [activeTab]
  );

  const categorySites = useMemo(
    () => sites.filter((s) => s.categorySlug === activeTab),
    [activeTab]
  );

  const colors = currentCategory
    ? categoryColorMap[currentCategory.color] || categoryColorMap.primary
    : categoryColorMap.primary;

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading text-foreground-900 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 网址导航 | 九色导航`}
        description={`九色导航${currentCategory.name}分类，${currentCategory.description}。收录${categorySites.length}个优质网站，帮您快速找到所需的${currentCategory.name}网站资源。`}
        keywords={`${currentCategory.name},${currentCategory.name}网址导航,${currentCategory.name}网站推荐,网址导航,九色导航`}
        canonicalUrl={`/category/${currentCategory.slug}`}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <header className="pt-24 pb-8 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-screen-2xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                <i className={`${currentCategory.icon} text-2xl ${colors.text}`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {currentCategory.description} · 共收录 <strong>{categorySites.length}</strong> 个网站
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="py-8 px-4 md:px-6">
          <div className="max-w-screen-2xl mx-auto">
            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sites Grid */}
            {categorySites.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {categorySites.map((site) => (
                  <SiteCard key={site.id} site={site} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">暂无网站，敬请期待</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}